import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { MessageCircle, Mail, Phone, Users, Search, ExternalLink, AlertCircle, CheckCircle2, Send, History, Clock } from 'lucide-react';
import { canEdit } from '@/lib/auth';
import api from '@/lib/api';

export default function Communication() {
  const [defaulters, setDefaulters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [currentDefaulter, setCurrentDefaulter] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [summary, setSummary] = useState({ total_count: 0, total_amount: 0 });
  const [contactHistory, setContactHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchDefaulters();
  }, []);

  const fetchDefaulters = async () => {
    try {
      const response = await api.get('/communication/defaulters-for-contact');
      setDefaulters(response.data.defaulters || []);
      setSummary({
        total_count: response.data.total_count,
        total_amount: response.data.total_amount
      });
    } catch (error) {
      toast.error('Erro ao carregar inadimplentes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 11) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
    }
    return phone;
  };

  const handleOpenWhatsApp = (defaulter) => {
    if (defaulter.whatsapp_link) {
      window.open(defaulter.whatsapp_link, '_blank');
      logContact(defaulter.responsible_id, 'whatsapp');
    } else {
      toast.error('WhatsApp não cadastrado para este responsável');
    }
  };

  const handleSendEmail = (defaulter) => {
    if (defaulter.email) {
      const subject = encodeURIComponent('Lembrete de Mensalidade - DNACore');
      const body = encodeURIComponent(defaulter.suggested_message);
      window.open(`mailto:${defaulter.email}?subject=${subject}&body=${body}`, '_blank');
      logContact(defaulter.responsible_id, 'email');
    } else {
      toast.error('Email não cadastrado para este responsável');
    }
  };

  const logContact = async (responsibleId, contactType) => {
    try {
      await api.post(`/communication/log-contact?responsible_id=${responsibleId}&contact_type=${contactType}`);
      toast.success('Contato registrado!');
    } catch (error) {
      console.error('Erro ao registrar contato:', error);
    }
  };

  const fetchContactHistory = async (responsibleId) => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/communication/contact-history/${responsibleId}`);
      setContactHistory(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
      setContactHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewHistory = async (defaulter) => {
    setCurrentDefaulter(defaulter);
    setHistoryDialogOpen(true);
    await fetchContactHistory(defaulter.responsible_id);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getContactIcon = (type) => {
    switch(type) {
      case 'whatsapp': return <MessageCircle className="w-4 h-4 text-green-600" />;
      case 'email': return <Mail className="w-4 h-4 text-blue-600" />;
      case 'phone': return <Phone className="w-4 h-4 text-gray-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleViewMessage = (defaulter) => {
    setCurrentDefaulter(defaulter);
    setCustomMessage(defaulter.suggested_message);
    setDialogOpen(true);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredDefaulters.map(d => d.responsible_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const filteredDefaulters = defaulters.filter(d =>
    d.responsible_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.student_names.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="communication-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Comunicação</h1>
        <p className="text-muted-foreground mt-1">Contate famílias com mensalidades em atraso</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <Users className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Famílias Inadimplentes</p>
                <p className="text-2xl font-bold">{summary.total_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total em Atraso</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.total_amount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selecionados</p>
                <p className="text-2xl font-bold">{selectedIds.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Busca e Ações em Massa */}
      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome do responsável ou aluno..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-defaulters"
              />
            </div>
            {selectedIds.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    selectedIds.forEach(id => {
                      const d = defaulters.find(x => x.responsible_id === id);
                      if (d?.whatsapp_link) window.open(d.whatsapp_link, '_blank');
                    });
                  }}
                  data-testid="bulk-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp ({selectedIds.length})
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Inadimplentes */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Famílias para Contato</CardTitle>
          <CardDescription>
            {filteredDefaulters.length} famílias com mensalidades em atraso
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredDefaulters.length === 0 ? (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Parabéns! Não há famílias com mensalidades em atraso no momento.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedIds.length === filteredDefaulters.length && filteredDefaulters.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Aluno(s)</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead className="text-center">Títulos</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-40">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDefaulters.map((defaulter) => (
                    <TableRow key={defaulter.responsible_id} data-testid={`defaulter-row-${defaulter.responsible_id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(defaulter.responsible_id)}
                          onCheckedChange={(checked) => handleSelectOne(defaulter.responsible_id, checked)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{defaulter.responsible_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {defaulter.student_names.map((name, idx) => (
                            <span key={idx} className="block">{name}</span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {defaulter.whatsapp && (
                            <div className="flex items-center gap-1 text-green-600">
                              <MessageCircle className="w-3 h-3" />
                              {formatPhone(defaulter.whatsapp)}
                            </div>
                          )}
                          {defaulter.email && (
                            <div className="flex items-center gap-1 text-blue-600">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-32">{defaulter.email}</span>
                            </div>
                          )}
                          {!defaulter.whatsapp && !defaulter.email && (
                            <span className="text-muted-foreground">Não cadastrado</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          {defaulter.titles_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-red-600">
                        {formatCurrency(defaulter.total_overdue)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenWhatsApp(defaulter)}
                            disabled={!defaulter.whatsapp}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Abrir WhatsApp"
                            data-testid={`whatsapp-${defaulter.responsible_id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSendEmail(defaulter)}
                            disabled={!defaulter.email}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Enviar Email"
                            data-testid={`email-${defaulter.responsible_id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewMessage(defaulter)}
                            title="Ver Mensagem"
                            data-testid={`view-message-${defaulter.responsible_id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewHistory(defaulter)}
                            title="Histórico de Contatos"
                            data-testid={`history-${defaulter.responsible_id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para ver/editar mensagem */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mensagem de Cobrança</DialogTitle>
            <DialogDescription>
              Para: {currentDefaulter?.responsible_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p><strong>Aluno(s):</strong> {currentDefaulter?.student_names.join(', ')}</p>
              <p><strong>Total em atraso:</strong> {formatCurrency(currentDefaulter?.total_overdue || 0)}</p>
              <p><strong>Títulos:</strong> {currentDefaulter?.titles_count}</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Mensagem:</label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={10}
                className="font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Fechar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (currentDefaulter?.email) {
                  const subject = encodeURIComponent('Lembrete de Mensalidade - DNACore');
                  const body = encodeURIComponent(customMessage);
                  window.open(`mailto:${currentDefaulter.email}?subject=${subject}&body=${body}`, '_blank');
                  logContact(currentDefaulter.responsible_id, 'email');
                }
              }}
              disabled={!currentDefaulter?.email}
              className="text-blue-600"
            >
              <Mail className="w-4 h-4 mr-2" />
              Enviar Email
            </Button>
            <Button
              onClick={() => {
                if (currentDefaulter?.whatsapp) {
                  const phone = currentDefaulter.whatsapp.replace(/\D/g, '');
                  const fullPhone = phone.length <= 11 ? `55${phone}` : phone;
                  const link = `https://wa.me/${fullPhone}?text=${encodeURIComponent(customMessage)}`;
                  window.open(link, '_blank');
                  logContact(currentDefaulter.responsible_id, 'whatsapp');
                }
              }}
              disabled={!currentDefaulter?.whatsapp}
              className="bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Histórico de Contatos */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Histórico de Contatos
            </DialogTitle>
            <DialogDescription>
              {currentDefaulter?.responsible_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : contactHistory.length === 0 ? (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  Nenhum contato registrado ainda.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {contactHistory.map((contact, idx) => (
                  <div key={contact.id || idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="mt-0.5">
                      {getContactIcon(contact.contact_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <span className="font-medium capitalize">
                          {contact.contact_type === 'whatsapp' ? 'WhatsApp' : 
                           contact.contact_type === 'email' ? 'Email' : 
                           contact.contact_type === 'phone' ? 'Telefone' : contact.contact_type}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(contact.contacted_at)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Por: {contact.contacted_by}
                      </p>
                      {contact.notes && (
                        <p className="text-sm mt-1">{contact.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
